import React, { useState, useEffect, useMemo } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import MonacoEditor from '@monaco-editor/react';
import yaml from 'js-yaml';

import { useColorMode } from './../../../contexts';

import { CustomEditorProps } from '.';

const SigmaYAMLEditor: React.FC<CustomEditorProps> = ({ value, onChange, readonly }) => {
  const [isPreview, setIsPreview] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { mode } = useColorMode();

  const errorMsg = useMemo(() => {
    try {
      yaml.load(value || '');
      return null;
    } catch (e: unknown) {
      return (e as Error).message;
    }
  }, [value]);

  useEffect(() => {
    setError(errorMsg);
  }, [errorMsg]);

  useEffect(() => {
    if (readonly) setIsPreview(true);
  }, [readonly]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">YAML</Typography>
        {!readonly && (
          <Tooltip title={isPreview ? 'Edit' : 'Preview'}>
            <IconButton onClick={() => setIsPreview(prev => !prev)}>
              {isPreview ? <EditIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {!isPreview && error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <MonacoEditor
        height="200px"
        language="yaml"
        value={value || ''}
        onChange={v => !isPreview && onChange(v || '')}
        theme={mode === 'dark' ? 'vs-dark' : 'light'}
        options={{
          readOnly: isPreview || readonly,
          minimap: { enabled: false },
          fontFamily: 'Roboto Mono, monospace',
          fontSize: 16,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true
        }}
      />
    </Box>
  );
};

export default SigmaYAMLEditor;
